import { Component, Inject, Input, OnInit } from '@angular/core';
import { ModalService } from '@purespectrum1/ui/modal';
import { GlobalConstants } from '../../shared/constants/global.constants';
import { notifyMessage } from '../../shared/constants/notify-message';
import { EnvironmentConfig } from '../../shared/interfaces/environment.interface';
import { AuthService } from '../../shared/services/auth/auth.service';
import {
  BuyerSurveyResponse,
  Survey,
  UpdateSurveyFieldsApiResponse,
} from '../../shared/interfaces/survey.interface';
import { BuyerApiService } from '../../shared/services/buyer-api/survey.service';
import { ApiStatusAndMessageResponse } from '../../shared/services/survey/survey.interface';
import { SurveyService } from '../../shared/services/survey/survey.service';
import { ConfirmationModalComponent } from '../../shared/ui/confirmation-modal/confirmation-modal.component';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { Constants } from '../constants/manage-links.constants';
import { SpaceValidator } from '../../shared/validators/survey/space-validator';
import { catchError, map, tap } from 'rxjs/operators';
import { SurveyUpdateError } from '../../shared/services/buyer-api/domain/survey-update-error';
import { Observable, of, throwError } from 'rxjs';
import { BillingRulesService } from '../../shared/services/billing-rules/billing-rules-service';
import { BillingNumberRules } from '../../shared/interfaces/billing-rules.interfaces';
import { SURVEY_STATUS } from '../../shared/constants/survey-status';
import { McSvpParentChildSegregator } from '../../shared/domain/mc-svp-parent-child-segregator';

@Component({
  selector: 'ps-ui-manage-links',
  templateUrl: './manage-links.component.html',
  styleUrls: ['./manage-links.component.css'],
})
export class ManageLinksComponent implements OnInit {
  @Input() survey: Survey = {} as Survey;
  @Input() surveyId!: number | undefined;
  @Input() selectedSvpChildSurvey!: Survey;
  private _cmpId?: number;
  private _baseUrl: string = '';
  public buyerTestEntryLink: string = '';
  public suppEntryLink: string = '';
  public showCRMIdInput: boolean = true;
  public CRMId: string = '';
  readonly EMPTY_CRMID_ERROR_TEXT: string = Constants.EMPTY_CRMID_ERROR_TEXT;
  public showCrmError: boolean = false;
  public crmInputFocused: boolean = false;
  public ASANAId: string = '';
  public asanaInputFocused: boolean = false;
  public showASANAIdInput: boolean = true;
  public showASANAError: boolean = false;
  readonly EMPTY_ASANAID_ERROR_TEXT: string =
    Constants.EMPTY_ASANAID_ERROR_TEXT;
  public isEditBillingNumber: boolean = false;
  public initialBillingId: string = '';
  public billingRules$: Observable<BillingNumberRules>;
  public billingNumberRules?: BillingNumberRules;
  public mcSvpChildSurveys: BuyerSurveyResponse[] = [];
  public aidaEligibility$: Observable<boolean>;

  constructor(
    private _auth: AuthService,
    private _modal: ModalService,
    private _buyerApiService: BuyerApiService,
    private _surveyService: SurveyService,
    private _toastr: ToasterService,
    private _billingRulesService: BillingRulesService,
    @Inject('ENV') private _env: EnvironmentConfig
  ) {
    this._baseUrl = _env.environment.pureSpectrum.url;
    this._cmpId = this._auth.user?.cmp;
  }

  ngOnInit(): void {
    this._generateEntryLinks();
    this._initializeCRMState();
    this._excludeParentSurveyFromExclusion();
    this._initializeASANAState();
    this._initBillingRules();
    this._fetchMcSpvChildrenData();

    this.aidaEligibility$ = this._aidaEleibility();
  }

  private _initBillingRules() {
    this.billingRules$ = this._billingRulesService
      .fetchRules(this.survey.companyId)
      .pipe(
        tap((rules) => {
          this.billingNumberRules = rules;
        })
      );
  }

  private _generateEntryLinks() {
    this.suppEntryLink = `${this._baseUrl}/#/start-survey?survey_id=${this.surveyId}&supplier_id=${this._cmpId}`;
    this.buyerTestEntryLink = this.getBuyerTestEntryLink(this.surveyId!);
  }

  public getBuyerTestEntryLink(surveyId: number) {
    let id = !!this.selectedSvpChildSurvey
      ? this.selectedSvpChildSurvey.projectId
      : surveyId;
    let url = `${this._baseUrl}/#/start-survey?survey_id=${id}&ps_redirect_test=1&bsec=${GlobalConstants.BSEC}&supplier_id=${this._env.environment.BUYER_TEST_LINK_SUPPLIER_ID}`;
    if (!!this.selectedSvpChildSurvey) {
      url += `&localization=${this.selectedSvpChildSurvey.survey_localization}`;
    }
    return url;
  }

  private _initializeCRMState() {
    this.showCRMIdInput = !this.survey.crmId;
  }
  private _initializeASANAState() {
    this.showASANAIdInput = !this.survey.asanaId;
  }

  private _excludeParentSurveyFromExclusion() {
    if (this.surveyId && this.survey.survey_grouping) {
      const parentSurveyIndex = this.survey.survey_grouping.indexOf(
        Number(this.surveyId)
      );
      if (parentSurveyIndex !== -1) {
        this.survey.survey_grouping.splice(parentSurveyIndex, 1);
      }
    }
    if (
      this.surveyId &&
      this.survey.supplier_survey_grouping &&
      this.survey.supplier_survey_grouping.survey_ids
    ) {
      const parentSurveyIndex =
        this.survey.supplier_survey_grouping.survey_ids.indexOf(
          Number(this.surveyId)
        );
      if (parentSurveyIndex !== -1) {
        this.survey.supplier_survey_grouping.survey_ids.splice(
          parentSurveyIndex,
          1
        );
      }
    }
  }

  public showModalBeforeBlockCreation() {
    const modalRef = this._modal.open(ConfirmationModalComponent, {
      data: {
        text: Constants.CONFIRM_MODAL_HEADER_TEXT,
      },
    });
    modalRef.onClose$.subscribe((msg: string) => {
      if (msg === 'ok') {
        this.createNewPriceBlock();
      }
    });
  }

  public createNewPriceBlock() {
    const surveyId = this.selectedSvpChildSurvey?.id ?? this.surveyId;
    this._surveyService.createNewPriceBlock(surveyId).subscribe(
      (response: ApiStatusAndMessageResponse) => {
        this._toastr.info(response.msg);
      },
      (error: any) => {
        this._toastr.error(error.error.msg);
      }
    );
  }

  public showModalBeforeResetFusionBlockStats() {
    const modalRef = this._modal.open(ConfirmationModalComponent, {
      data: {
        text: Constants.CONFIRM_MODAL_HEADER_TEXT,
      },
    });
    modalRef.onClose$.subscribe((msg: string) => {
      if (msg === 'ok') {
        this.resetFusionBlockStats();
      }
    });
  }

  public resetFusionBlockStats() {
    const surveyId = this.selectedSvpChildSurvey?.id ?? this.surveyId;
    this._surveyService.resetFusionBlockStats(surveyId).subscribe(
      (response: ApiStatusAndMessageResponse) => {
        this._toastr.success(response.msg);
      },
      (error: any) => {
        this._toastr.error(error.error.msg);
      }
    );
  }

  public enableEditCRMId(initialValue?: string) {
    if (initialValue && this.showCRMIdInput) {
      this.showCRMIdInput = false;
    } else if (initialValue) {
      this.showCRMIdInput = true;
      this.CRMId = initialValue;
      this.showCrmError = false;
      this.crmInputFocused = true;
    } else {
      this.showCRMIdInput = true;
      this.CRMId = '';
      this.showCrmError = false;
      this.crmInputFocused = false;
    }
  }

  public enableEditASANAId(initialValue?: string) {
    if (initialValue && this.showCRMIdInput) {
      this.showASANAIdInput = false;
    } else if (initialValue) {
      this.showASANAIdInput = true;
      this.ASANAId = initialValue;
      this.showASANAError = false;
      this.asanaInputFocused = true;
    } else {
      this.showASANAIdInput = true;
      this.ASANAId = '';
      this.showASANAError = false;
      this.asanaInputFocused = false;
    }
  }

  public saveCRMId(CRMId: string) {
    if (!CRMId || CRMId.length < 1) {
      this.showCRMIdInput = true;
      this.showCrmError = true;
      return;
    }
    const payload = this._generateUpdatePayload(CRMId, 'crmId');
    this._updateSurveyFields(payload, 'crmId', CRMId);
  }

  public saveASANAId(ASANAID: string) {
    if (!ASANAID || ASANAID.length < 1) {
      this.showASANAIdInput = true;
      this.showASANAError = true;
      return;
    }
    const payload = this._generateUpdatePayload(ASANAID, 'asanaId');
    this._updateSurveyFields(payload, 'asanaId', ASANAID);
  }

  private _generateUpdatePayload(id: string, type: string) {
    let payload = {
      op: 'replace',
      path: '',
      value: id,
    };
    payload.path = '/' + type;
    return [payload];
  }
  private _updateSurveyFields(payload: any, type: string, id: string) {
    this._buyerApiService.updateSurveyField(this.surveyId!, payload).subscribe(
      (response: UpdateSurveyFieldsApiResponse) => {
        if (type === 'crmId') {
          this.survey.crmId = id;
          this.showCRMIdInput = false;
        } else {
          this.survey.asanaId = id;
          this.showASANAIdInput = false;
        }
      },
      (error: any) => {
        if (type === 'crmId') {
          this.showCRMIdInput = true;
          this._toastr.error(
            notifyMessage.errorMessage.ID_UPLOAD_ERROR.CRM_UPDATE_ERROR
          );
        } else {
          this.showASANAIdInput = true;
          this._toastr.error(
            notifyMessage.errorMessage.ID_UPLOAD_ERROR.ASANA_UPDATE_ERROR
          );
        }
      }
    );
  }

  public enableEditBillingNumber() {
    if (this._auth.userType === 'supplier') {
      // not allowing supplier to edit the billing number
      return;
    }
    this.isEditBillingNumber = true;
    this.initialBillingId = this.survey.billing_id || '';
  }

  public onBillingNumberChange(event: string) {
    this.survey.billing_id = event;
  }

  public cancelBillingNumber() {
    this.survey.billing_id = this.initialBillingId;
    this.isEditBillingNumber = false;
  }

  private _validateAndRemoveSpacesFromBillingNumber(
    billingNumber: string = ''
  ): void {
    const validator = new SpaceValidator(billingNumber);
    if (validator.check()) {
      this.survey.billing_id = validator.remove();
      return this._toastr.error(
        notifyMessage.errorMessage.PO_NUMBER_SPACES_NOT_ALLOWED,
        notifyMessage.errorMessage.PO_NUMBER_SPACE_NOT_ALLOWER_TITLE
      );
    }
  }

  private _validateMaxCharLimitOfBillingNumber(): boolean {
    if (
      this.survey.billing_id &&
      this.survey.billing_id.length > GlobalConstants.MAX_ALLOWED_CHARS_FOR_PO
    ) {
      this._toastr.error(
        notifyMessage.errorMessage.PO_CHARACTER_LIMIT_EXCEED.replace(
          '<MAX_CHAR>',
          GlobalConstants.MAX_ALLOWED_CHARS_FOR_PO.toString()
        )
      );
      return false;
    }
    return true;
  }

  public updateBillingNumber() {
    this._validateAndRemoveSpacesFromBillingNumber(this.survey.billing_id);
    const isValid = this._validateMaxCharLimitOfBillingNumber();
    if (!isValid) {
      return;
    }
    const payload = [
      {
        op: 'replace',
        path: '/billing_id',
        value: this.survey.billing_id || '',
      },
    ];
    this._buyerApiService
      .patchSurvey(this.surveyId!, payload)
      .pipe(
        catchError((error) =>
          throwError(new SurveyUpdateError(error, this._auth.buyerConfig!))
        )
      )
      .subscribe(
        (__response: UpdateSurveyFieldsApiResponse) => {
          this.isEditBillingNumber = false;
          this.initialBillingId = this.survey.billing_id || '';
          this._toastr.success(
            notifyMessage.successMessage.BILLING_NUMBER_UPDATED
          );
        },
        (error) => {
          this._toastr.error(error.message);
        }
      );
  }

  private _fetchMcSpvChildrenData(): void {
    if (!this.survey.mc?.SVP) {
      return;
    }

    const { mc_unique_id } = this.survey;

    const surveyStatus = Object.values(SURVEY_STATUS)
      .filter((status) => typeof status === 'number')
      .join(',');

    this._buyerApiService
      .getSurveys(surveyStatus, 1, '', mc_unique_id, 1)
      .pipe(map((data) => new McSvpParentChildSegregator(data).childs()))
      .subscribe((data) => {
        this.mcSvpChildSurveys = data;
      });
  }

  public _aidaFeatureEnabled(): boolean {
    return (
      !!this._auth.buyerConfig?.enableAiDeliveryAssistance ||
      (this._auth.userType === 'operator' &&
        !this._auth.isLoggedInAsServiceBuyer())
    );
  }

  public _aidaEleibility(): Observable<boolean> {
    if (this.survey.aida?.enabled) {
      return of(true);
    }

    return of(false);
  }
}
